import type { AreaReport } from "@corklog/shared";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import {
  ApiError,
  fetchAreaReport,
  fetchCellars,
  fetchProducers,
  publishAreaReport,
} from "../api.js";
import { heldMarks, type CellarProducers, type HeldMark } from "../area-match.js";
import { AreaReportView } from "../components/AreaReportView.js";
import { useReload } from "../offline.js";
import { useSession } from "../session.js";

type State =
  | { status: "loading" }
  | { status: "ready"; report: AreaReport }
  | { status: "missing" }
  | { status: "switchedOff" }
  | { status: "failed" };

/**
 * One area report — a published one for anybody, and a draft for the
 * administrator, who reads it here before publishing it. Tasks 10.32 and
 * 10.34; decided 2026-09-28 in `project-scope.md`.
 *
 * **The marks are fetched after the report**, from the reader's own cellars,
 * and the report is drawn without waiting for them: they are a convenience on
 * top of a page that is complete without them, and a reader with several
 * cellars should not wait for every producer list before reading about
 * Champagne.
 */
export function AreaReportPage() {
  const { t } = useTranslation();
  const { reportId = "" } = useParams();
  const session = useSession();
  const isAdministrator = session.status === "signedIn" && session.user.isAdministrator;
  const [state, setState] = useState<State>({ status: "loading" });
  const [held, setHeld] = useState<Map<string, HeldMark> | undefined>(undefined);
  const [publishFailed, setPublishFailed] = useState(false);

  const load = useCallback(() => {
    void fetchAreaReport(reportId)
      .then((report) => setState({ status: "ready", report }))
      .catch((error: unknown) =>
        setState({
          status:
            error instanceof ApiError && error.status === 404 ? "missing" : "failed",
        }),
      );
  }, [reportId]);

  useReload(load);

  const report = state.status === "ready" ? state.report : null;
  useEffect(() => {
    if (report === null) return;
    let current = true;
    const producers = report.content.regions.flatMap((region) => region.producers);
    void fetchCellars()
      .then(({ cellars }) =>
        Promise.all(
          cellars.map(async (cellar): Promise<CellarProducers> => ({
            cellarId: cellar.id,
            cellarName: cellar.name,
            producers: await fetchProducers(cellar.id),
          })),
        ),
      )
      .then((cellars) => {
        if (current) setHeld(heldMarks(producers, cellars));
      })
      // Without the marks the page is still the report; nothing to say.
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [report]);

  const publish = (draft: AreaReport) => {
    setPublishFailed(false);
    void publishAreaReport(draft.id)
      .then(load)
      .catch(() => setPublishFailed(true));
  };

  return (
    <section className="space-y-6">
      <p>
        <Link
          to={
            isAdministrator && report?.status !== "published"
              ? "/installningar/omraden"
              : "/upptack"
          }
          className="text-accent text-sm underline-offset-4 hover:underline"
        >
          {isAdministrator && report?.status !== "published"
            ? t("areaReports.title")
            : t("discover.title")}
        </Link>
      </p>

      {state.status === "loading" && <p className="text-muted text-sm">{t("loading")}</p>}
      {state.status === "missing" && (
        <p className="text-muted max-w-prose text-sm">{t("discover.missing")}</p>
      )}
      {state.status === "switchedOff" && (
        <p className="text-muted max-w-prose text-sm">{t("discover.switchedOff")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past text-sm">
          {t("discover.reportFailed")}
        </p>
      )}

      {report !== null && (
        <>
          {report.status !== "published" && (
            // Only the administrator ever reaches a draft; the route answers
            // anybody else 404. Said above the report, since it is the one
            // thing about this page that differs from what readers will see.
            <div className="border-accent flex flex-wrap items-center justify-between gap-3 rounded-sm border border-dashed px-4 py-3 text-sm">
              <span className="text-muted">
                {report.wasPublished
                  ? t("areaReports.draftWasPublished")
                  : t("areaReports.draftNotice")}
              </span>
              <button
                type="button"
                onClick={() => publish(report)}
                className="text-accent font-semibold underline-offset-4 hover:underline"
              >
                {t("areaReports.publish")}
              </button>
            </div>
          )}
          {/* No unpublishing here: since 2026-09-29 it is done from the
              administrator's view alone, beside the run, so a published report
              reads the same for the administrator as for anybody. */}
          {publishFailed && (
            <p role="alert" className="text-past text-sm">
              {t("areaReports.actionFailed")}
            </p>
          )}
          {/* The fact check is not here: it moved to the administrator's view
              on 2026-09-29, beside publishing, so this page reads as the
              readers will read it. */}
          <AreaReportView report={report} {...(held === undefined ? {} : { held })} />
        </>
      )}
    </section>
  );
}
